import { useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { useLang } from "@/i18n";

export const BeforeAfterSlider = ({ before, after, aspect = "aspect-[16/9]" }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const { t } = useLang();

  const update = (clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect || clientX == null) return;
    setPos(Math.min(97, Math.max(3, ((clientX - rect.left) / rect.width) * 100)));
  };

  const startDrag = (e) => {
    update(e.clientX);
    const move = (ev) => update(ev.clientX);
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  };

  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(3, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(97, p + 4));
  };

  return (
    <div
      ref={ref}
      role="slider"
      tabIndex={0}
      aria-valuenow={Math.round(pos)}
      aria-label="Before and after comparison"
      data-testid="before-after-slider"
      onPointerDown={startDrag}
      onKeyDown={onKey}
      className={`relative ${aspect} overflow-hidden select-none cursor-ew-resize touch-none bg-[#121216] border border-white/10 outline-none transition-colors duration-300 focus:border-amber-500/50`}
    >
      <img src={after} alt={t("ba_after")} draggable={false} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img
          src={before}
          alt={t("ba_before")}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover saturate-[0.55]"
        />
      </div>

      <span className="absolute top-4 left-4 text-[10px] tracking-[0.25em] uppercase font-mono border border-zinc-400/30 bg-black/50 text-zinc-300 px-3 py-1.5 backdrop-blur pointer-events-none">
        {t("ba_before")}
      </span>
      <span className="absolute top-4 right-4 text-[10px] tracking-[0.25em] uppercase font-mono border border-amber-500/40 bg-black/50 text-[#D4AF37] px-3 py-1.5 backdrop-blur pointer-events-none">
        {t("ba_after")}
      </span>

      <div className="absolute inset-y-0 pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -left-px w-0.5 bg-[#D4AF37]" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full border border-[#D4AF37] bg-[#0A0A0C]/85 backdrop-blur flex items-center justify-center text-[#D4AF37] shadow-[0_0_24px_rgba(212,175,55,0.35)]">
          <ChevronsLeftRight size={18} />
        </div>
      </div>
    </div>
  );
};
